// meetingSlice.ts
//@ts-nocheck
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  ApiError,
  MeetingResponse,
  TeamsState,
  createTeamsPayload,
  updateTeamsPayload,
} from "./AppReviewTypes";
import {
  createAppReviewData,
  deleteAppReview,
  fetchReviewData,
  fetchAppReviewDetails,
  updateAppReview,
  fetchAppJourney,
  updateInternalQuestionValue,
  createEvaluationData,
  updateEvaluationQuestionAnswer
} from "../../services/appReviewService";

export const fetchAppReviewList = createAsyncThunk<
  MeetingResponse,
  { baseId: number; appId: number },
  { rejectValue: string }
>("base/fetchAppReviewList", async ({ baseId, appId }, { rejectWithValue }) => {
  try {
    if (baseId === undefined || appId === undefined) {
      throw new Error("Base ID or App ID is undefined");
    }
    const response = await fetchReviewData(baseId, appId);
    return response;
  } catch (err) {
    const error = err as Error;
    return rejectWithValue(error.message || "Failed to fetch base data");
  }
});

export const createAppReview = createAsyncThunk<
  MeetingResponse,
  { payload: createTeamsPayload; userId: number },
  { rejectValue: string }
>("base/createAppReview", async ({ payload, userId }, { rejectWithValue }) => {
  try {
    const response = await createAppReviewData(payload, userId);
    return response;
  } catch (err) {
    const error = err as ApiError;
    return rejectWithValue(
      error.response?.data?.message || "Failed to invite member"
    );
  }
});
export const createEvaluation = createAsyncThunk<
  MeetingResponse,
  { payload: createTeamsPayload; userId: number },
  { rejectValue: string }
>("base/createEvaluation", async ({ payload, userId }, { rejectWithValue }) => {
  try {
    const response = await createEvaluationData(payload, userId);
    return response;
  } catch (err) {
    const error = err as ApiError;
    return rejectWithValue(
      error.response?.data?.message || "Failed to invite member"
    );
  }
});

export const deleteAppReviewData = createAsyncThunk<
  MeetingResponse,
  { baseId: number; id: number },
  { rejectValue: string }
>("base/deleteAppReviewData", async ({ baseId, id }, { rejectWithValue }) => {
  try {
    const response = await deleteAppReview(baseId, id);
    return response;
  } catch (err) {
    const error = err as ApiError;
    return rejectWithValue(
      error.response?.data?.message || "Failed to delete base member"
    );
  }
});

export const updateQuestionValue = createAsyncThunk<
  MeetingResponse,
  { data: any },
  { rejectValue: string }
>("base/updateQuestionValue", async ({ data }, { rejectWithValue }) => {
  try {
    const response = await updateInternalQuestionValue(data);
    return response;
  } catch (err) {
    const error = err as ApiError;
    return rejectWithValue(
      error.response?.data?.message || "Failed to delete base member"
    );
  }
});

export const updateEvaluationQuestionValue = createAsyncThunk<
  MeetingResponse,
  { data: any },
  { rejectValue: string }
>("base/updateEvaluationQuestionValue", async ({ data }, { rejectWithValue }) => {
  try {
    const response = await updateEvaluationQuestionAnswer(data);
    return response;
  } catch (err) {
    const error = err as ApiError;
    return rejectWithValue(
      error.response?.data?.message || "Failed to delete base member"
    );
  }
});

export const updateAppReviewData = createAsyncThunk<
  MeetingResponse,
  { baseId: number; id: number; payload: updateTeamsPayload },
  { rejectValue: string }
>(
  "base/updateAppReviewData",
  async ({ baseId, id, payload }, { rejectWithValue }) => {
    try {
      const response = await updateAppReview(baseId, id, payload);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(
        error.response?.data?.message || "Failed to update base member"
      );
    }
  }
);

export const fetchAppReviewDetailsData = createAsyncThunk<
  MeetingResponse,
  { baseId: number; id: number },
  { rejectValue: string }
>(
  "base/fetchAppReviewDetailsData",
  async ({ baseId, id }, { rejectWithValue }) => {
    try {
      const response = await fetchAppReviewDetails(baseId, id);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch base member"
      );
    }
  }
);

export const fetchAppJourneyData1 = createAsyncThunk<
  MeetingResponse,
  { baseId: number; appId: number },
  { rejectValue: string }
>(
  "base/fetchAppJourneyData1",
  async ({ baseId, appId }, { rejectWithValue }) => {
    try {
      const response = await fetchAppJourney(baseId, appId);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch base member"
      );
    }
  }
);

const initialState: TeamsState = {
  //meeting
  createMeetingStatus: "idle",
  createMeetingError: null,
  createAppReviewData: null,
  fetchMeetingStatus: "idle",
  fetchMeetingError: null,
  fetchReviewData: null,
  deleteMeetingStatus: "idle",
  deleteMeetingError: null,
  deleteAppReviewData: null,
  updateMeetingStatus: "idle",
  updateMeetingError: null,
  updateAppReviewData: null,
  fetchMeetingDetailStatus: "idle",
  fetchMeetingDetailError: null,
  fetchMeetingDetailData: null,
  fetchAppJourneyData: null,
  fetchAppJourneyStatus: "idle",
  fetchAppJourneyError: null,
};

const AppReviewSlice = createSlice({
  name: "appreview",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createAppReview.pending, (state) => {
        state.createMeetingStatus = "loading";
      })
      .addCase(
        createAppReview.fulfilled,
        (state, action: PayloadAction<MeetingResponse>) => {
          state.createMeetingStatus = "succeeded";
          state.createAppReviewData = action.payload;
        }
      )
      .addCase(createAppReview.rejected, (state, action) => {
        state.createMeetingStatus = "failed";
        state.createMeetingError = action.payload || "Failed to create meeting";
      })
      .addCase(fetchAppReviewList.pending, (state) => {
        state.fetchMeetingStatus = "loading";
      })
      .addCase(
        fetchAppReviewList.fulfilled,
        (state, action: PayloadAction<MeetingResponse>) => {
          state.fetchMeetingStatus = "succeeded";
          state.fetchReviewData = action.payload;
        }
      )
      .addCase(fetchAppReviewList.rejected, (state, action) => {
        state.fetchMeetingStatus = "failed";
        state.fetchMeetingError = action.payload || "Failed to fetch meeting";
      })
      .addCase(deleteAppReviewData.pending, (state) => {
        state.deleteMeetingStatus = "loading";
      })
      .addCase(
        deleteAppReviewData.fulfilled,
        (state, action: PayloadAction<MeetingResponse>) => {
          state.deleteMeetingStatus = "succeeded";
          state.deleteAppReviewData = action.payload;
        }
      )
      .addCase(deleteAppReviewData.rejected, (state, action) => {
        state.deleteMeetingStatus = "failed";
        state.deleteMeetingError = action.payload || "Failed to delete meeting";
      })
      .addCase(updateAppReviewData.pending, (state) => {
        state.updateMeetingStatus = "loading";
      })
      .addCase(
        updateAppReviewData.fulfilled,
        (state, action: PayloadAction<MeetingResponse>) => {
          state.updateMeetingStatus = "succeeded";
          state.updateAppReviewData = action.payload;
        }
      )
      .addCase(updateAppReviewData.rejected, (state, action) => {
        state.updateMeetingStatus = "failed";
        state.updateMeetingError = action.payload || "Failed to update meeting";
      })
      .addCase(fetchAppReviewDetailsData.pending, (state) => {
        state.fetchMeetingDetailStatus = "loading";
      })
      .addCase(
        fetchAppReviewDetailsData.fulfilled,
        (state, action: PayloadAction<MeetingResponse>) => {
          state.fetchMeetingDetailStatus = "succeeded";
          state.fetchMeetingDetailData = action.payload;
        }
      )
      .addCase(fetchAppReviewDetailsData.rejected, (state, action) => {
        state.fetchMeetingDetailStatus = "failed";
        state.fetchMeetingDetailError =
          action.payload || "Failed to fetch meeting";
      })
      .addCase(fetchAppJourneyData1.pending, (state) => {
        state.fetchAppJourneyStatus = "loading";
      })
      .addCase(
        fetchAppJourneyData1.fulfilled,
        (state, action: PayloadAction<MeetingResponse>) => {
          state.fetchAppJourneyStatus = "succeeded";
          state.fetchAppJourneyData = action.payload;
        }
      )
      .addCase(fetchAppJourneyData1.rejected, (state, action) => {
        state.fetchAppJourneyStatus = "failed";
        state.fetchAppJourneyError =
          action.payload || "Failed to fetch meeting";
      });
  },
});

export default AppReviewSlice.reducer;
